import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { COPFilterListMode } from './filterPredicates';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface FilterState {
  // Course & Student filters (shared between COP and Pathfinder)
  selectedCourses: string[]; // Empty = all courses
  selectedStudents: string[];
  availableCourses: { id: string; title: string }[];
  availableStudents: { id: string; name: string }[];
  
  // Device filters (arrays for multi-select)
  selectedDeviceTypes: string[];
  selectedEntityTypes: string[];
  selectedEntities: string[]; // Empty = all entities
  /** include = selected values pass; exclude = selected values are filtered out */
  courseFilterMode: COPFilterListMode;
  deviceTypeFilterMode: COPFilterListMode;
  entityTypeFilterMode: COPFilterListMode;
  entityFilterMode: COPFilterListMode;

  // Date/Time filters
  selectedDate: string; // For COP dropdown (Last Hour, Last 24 Hours, etc.)
  dateRange: DateRange; // For Pathfinder precise date range
  
  // Actions
  setSelectedCourses: (courses: string[]) => void;
  setSelectedStudents: (students: string[]) => void;
  addStudent: (studentId: string) => void;
  removeStudent: (studentId: string) => void;
  setAvailableCourses: (courses: { id: string; title: string }[]) => void;
  setAvailableStudents: (students: { id: string; name: string }[]) => void;
  setSelectedDeviceTypes: (deviceTypes: string[]) => void;
  setSelectedEntityTypes: (entityTypes: string[]) => void;
  setSelectedEntities: (entities: string[]) => void;
  setCourseFilterMode: (mode: COPFilterListMode) => void;
  setDeviceTypeFilterMode: (mode: COPFilterListMode) => void;
  setEntityTypeFilterMode: (mode: COPFilterListMode) => void;
  setEntityFilterMode: (mode: COPFilterListMode) => void;
  setSelectedDate: (date: string) => void;
  setDateRange: (range: DateRange) => void;
  resetFilters: () => void;
}

const defaultDateRange = (): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 2);
  return { start, end };
};

const initialState = {
  selectedCourses: [] as string[], // Empty = all courses
  selectedStudents: [] as string[],
  availableCourses: [] as { id: string; title: string }[],
  availableStudents: [] as { id: string; name: string }[],
  selectedDeviceTypes: [] as string[], // Empty = all types
  selectedEntityTypes: [] as string[], // Empty = all types
  selectedEntities: [] as string[], // Empty = all entities
  courseFilterMode: 'include' as COPFilterListMode,
  deviceTypeFilterMode: 'include' as COPFilterListMode,
  entityTypeFilterMode: 'include' as COPFilterListMode,
  entityFilterMode: 'include' as COPFilterListMode,
  selectedDate: 'Last Hour',
  dateRange: defaultDateRange(),
};

export const useFilterStore = create<FilterState>()(
  persist(
    (set) => ({
      ...initialState,

      // Clear students on course change (Pathfinder cascade). Keep selectedEntities
      // so COP can add companion courses (e.g. Exercise Support) without dropping
      // the itinerary subject; invalid IDs are pruned by dropdown cascade.
      setSelectedCourses: (courses: string[]) =>
        set({ selectedCourses: courses, selectedStudents: [] }),

      setSelectedStudents: (students: string[]) => 
        set({ selectedStudents: students }),

      addStudent: (studentId: string) =>
        set((state: FilterState) => ({
          selectedStudents: state.selectedStudents.includes(studentId)
            ? state.selectedStudents
            : [...state.selectedStudents, studentId],
        })),

      removeStudent: (studentId: string) =>
        set((state: FilterState) => ({
          selectedStudents: state.selectedStudents.filter((id: string) => id !== studentId),
        })),

      setAvailableCourses: (courses: { id: string; title: string }[]) => 
        set({ availableCourses: courses }),

      setAvailableStudents: (students: { id: string; name: string }[]) => 
        set({ availableStudents: students }),

      setSelectedDeviceTypes: (deviceTypes: string[]) => 
        set({ selectedDeviceTypes: deviceTypes }),

      setSelectedEntityTypes: (entityTypes: string[]) => 
        set({ selectedEntityTypes: entityTypes }),

      setSelectedEntities: (entities: string[]) => 
        set({ selectedEntities: entities }),

      setCourseFilterMode: (mode: COPFilterListMode) => set({ courseFilterMode: mode }),
      setDeviceTypeFilterMode: (mode: COPFilterListMode) => set({ deviceTypeFilterMode: mode }),
      setEntityTypeFilterMode: (mode: COPFilterListMode) => set({ entityTypeFilterMode: mode }),
      setEntityFilterMode: (mode: COPFilterListMode) => set({ entityFilterMode: mode }),

      setSelectedDate: (date: string) => 
        set({ selectedDate: date }),

      setDateRange: (range: DateRange) => 
        set({ dateRange: range }),

      resetFilters: () => 
        set(initialState),
    }),
    {
      name: 'filter-storage',
      merge: (persisted, current) => {
        const p = persisted as Partial<FilterState>;
        return {
          ...current,
          ...p,
          courseFilterMode: p.courseFilterMode ?? 'include',
          deviceTypeFilterMode: p.deviceTypeFilterMode ?? 'include',
          entityTypeFilterMode: p.entityTypeFilterMode ?? 'include',
          entityFilterMode: p.entityFilterMode ?? 'include',
        };
      },
      partialize: (state: FilterState) => ({
        selectedCourses: state.selectedCourses,
        selectedDate: state.selectedDate,
        selectedDeviceTypes: state.selectedDeviceTypes,
        selectedEntityTypes: state.selectedEntityTypes,
        courseFilterMode: state.courseFilterMode,
        deviceTypeFilterMode: state.deviceTypeFilterMode,
        entityTypeFilterMode: state.entityTypeFilterMode,
        entityFilterMode: state.entityFilterMode,
      }),
    }
  )
);
